import { useTranslation } from 'react-i18next';

import type { LogEntry, ProductionState } from '@/hooks/useProduction';

/** A long production emits thousands of lines; rendering them all locks the main thread. */
const TAIL = 250;

type Severity = 'error' | 'warning' | 'tuning' | 'director' | 'delegation' | 'crew' | 'plain';

/**
 * Classify a log line by severity.
 *
 * IMPORTANT: these markers are emitted by the PYTHON ENGINE and are always English,
 * regardless of the UI language. Classification must therefore run on the raw server
 * text — only the chrome around the log is translated.
 */
export function classify(log: LogEntry): Severity {
  const msg = log.message || '';
  const U = msg.toUpperCase();

  // ERROR = hard failures the run can't proceed through.
  if (
    log.event === 'error' ||
    /\bFATAL\b/.test(U) ||
    U.includes('WRONG PERSON') ||
    U.includes('WRONG LOCATION') ||
    U.includes('WRONG DIALOG') ||
    U.includes('CATASTROPHIC') ||
    msg.trimStart().startsWith('✗')
  ) {
    return 'error';
  }
  // WARNING = advisories worth noticing that don't stop the run.
  if (
    U.includes('COVERAGE GAP') ||
    /\bWARN(ING)?\b/.test(U) ||
    msg.includes('⚠') ||
    U.includes('FIDELITY') ||
    U.includes('DID NOT PASS') ||
    U.includes('ADVISORY') ||
    U.includes('FALLING BACK')
  ) {
    return 'warning';
  }
  if (msg.includes('[TUNING]') || msg.includes('Director tuned')) return 'tuning';
  if (msg.startsWith('[director]')) return 'director';
  if (msg.startsWith('[') && msg.includes(']')) return 'delegation';
  if (msg.startsWith('  ')) return 'crew';
  return 'plain';
}

const SEVERITY_CLS: Record<Severity, string> = {
  error: 'border-l-2 border-destructive/60 bg-destructive/5 pl-2 font-semibold text-destructive',
  warning: 'border-l-2 border-amber-500/60 bg-amber-500/5 pl-2 font-medium text-amber-600 dark:text-amber-400',
  tuning: 'font-medium text-emerald-600 dark:text-emerald-400',
  director: 'font-medium text-foreground',
  delegation: 'text-blue-600 dark:text-blue-400',
  crew: 'border-l-2 border-primary/20 pl-3 text-muted-foreground',
  plain: 'text-muted-foreground',
};

/**
 * Running commentary from the Director and the crew. Problems are colour-coded so a
 * failing run is obvious without reading every line.
 */
export default function DirectorLog({ logs }: { logs: ProductionState['logs'] }) {
  const { t } = useTranslation('production');
  const hidden = Math.max(0, logs.length - TAIL);

  return (
    <div className="flex h-full flex-col">
      <div
        className="flex-1 space-y-0.5 overflow-y-auto px-6 py-2 font-mono text-[11px] leading-relaxed"
        ref={(el) => {
          if (el) el.scrollTop = el.scrollHeight;
        }}
      >
        {logs.length === 0 && <div className="py-2 italic text-faint">{t('log.waiting')}</div>}
        {hidden > 0 && <div className="py-1 italic text-faint">{t('log.hidden', { count: hidden, shown: TAIL })}</div>}
        {logs.slice(-TAIL).map((log, i) => (
          <div key={i} className={SEVERITY_CLS[classify(log)]}>
            {log.message}
          </div>
        ))}
      </div>
    </div>
  );
}
